import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const categories = [
  { name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html', icon: 'ri-footprint-line' },
  { name: 'Hoodies/Sweaters', url: 'https://kaspreadsheet.com/category-912-Hoodies%2FSweaters.html', icon: 'ri-t-shirt-line' },
  { name: 'T-Shirts', url: 'https://kaspreadsheet.com/category-913-T-Shirts.html', icon: 'ri-shirt-line' },
  { name: 'Jackets', url: 'https://kaspreadsheet.com/category-914-Jackets.html', icon: 'ri-t-shirt-air-line' },
  { name: 'Pants/Shorts', url: 'https://kaspreadsheet.com/category-915-Pants%2FShorts.html', icon: 'ri-pantone-line' },
  { name: 'Headwear', url: 'https://kaspreadsheet.com/category-916-Headwear.html', icon: 'ri-capsule-line' },
  { name: 'Sets', url: 'https://kaspreadsheet.com/category-917-Sets.html', icon: 'ri-stack-line' },
  { name: 'Underwear/Underpants', url: 'https://kaspreadsheet.com/category-918-underwear%2Funderpants.html', icon: 'ri-checkbox-multiple-line' },
  { name: 'Jersey', url: 'https://kaspreadsheet.com/category-919-Jersey.html', icon: 'ri-basketball-line' },
  { name: 'Accessories', url: 'https://kaspreadsheet.com/category-920-Accessories.html', icon: 'ri-vip-diamond-line' },
  { name: 'Bags', url: 'https://kaspreadsheet.com/category-921-Bags.html', icon: 'ri-handbag-line' },
  { name: 'Belts', url: 'https://kaspreadsheet.com/search-922/name/Bags.html', icon: 'ri-link' },
  { name: 'Perfumes', url: 'https://kaspreadsheet.com/search-923/name/Bags.html', icon: 'ri-drop-line' },
  { name: 'Other Stuff', url: 'https://kaspreadsheet.com/search-924/name/Bags.html', icon: 'ri-more-line' },
];

const blogLinks = [
  { name: 'Beginner Guide', slug: '/blog/best-sugargoo-spreadsheet-2026-guide' },
  { name: 'How to Use Spreadsheets', slug: '/blog/how-to-use-sugargoo-spreadsheet' },
  { name: 'Sneaker Finds Top 50', slug: '/blog/top-50-sneaker-finds-sugargoo-spreadsheet' },
  { name: 'Clothing Finds', slug: '/blog/best-clothing-finds-sugargoo-spreadsheet' },
  { name: 'Budget Finds Under $20', slug: '/blog/best-budget-finds-sugargoo-spreadsheet' },
  { name: 'Sugargoo vs Pandabuy', slug: '/blog/sugargoo-spreadsheet-vs-pandabuy' },
  { name: 'How Sugargoo Works', slug: '/blog/how-sugargoo-works-2026' },
  { name: 'Hoodie Collection', slug: '/blog/best-hoodie-spreadsheet-collection' },
  { name: 'Yupoo Guide', slug: '/blog/yupoo-sugargoo-complete-workflow' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [blogOpen, setBlogOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const blogRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
      if (blogRef.current && !blogRef.current.contains(e.target as Node)) {
        setBlogOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  useEffect(() => {
    setMobileOpen(false);
    setIsOpen(false);
    setBlogOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  const closeMobileMenu = () => {
    setMobileOpen(false);
    setCategoryOpen(false);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-background-200/80'
            : 'bg-white/80 backdrop-blur-sm border-b border-transparent'
        }`}
      >
        <nav className="w-full px-4 md:px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <span className="font-heading text-lg md:text-xl font-extrabold text-foreground-950 tracking-tight">
              Sugargoo Spreadsheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            <Link
              to="/"
              className={`text-[15px] font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === '/' ? 'text-primary-600' : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              Home
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => { setIsOpen(!isOpen); setBlogOpen(false); }}
                className="flex items-center gap-1 text-[15px] font-semibold text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[640px] bg-white rounded-2xl border border-background-200/70 shadow-xl p-4 grid grid-cols-2 gap-1 z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-[14px] text-foreground-700 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <span className="w-7 h-7 flex items-center justify-center bg-primary-50 rounded-lg">
                        <i className={`${cat.icon} text-primary-600 text-xs`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div ref={blogRef} className="relative">
              <button
                onClick={() => { setBlogOpen(!blogOpen); setIsOpen(false); }}
                className="flex items-center gap-1 text-[15px] font-semibold text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
              >
                Shopping Guide
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${blogOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {blogOpen && (
                <div className="absolute top-full left-0 mt-2 w-[280px] bg-white rounded-2xl border border-background-200/70 shadow-xl p-3 flex flex-col gap-1 z-50">
                  {blogLinks.map((blog) => (
                    <Link
                      key={blog.slug}
                      to={blog.slug}
                      onClick={() => setBlogOpen(false)}
                      className="px-4 py-2.5 text-[14px] text-foreground-700 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {blog.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/safety-guide" className="text-[15px] font-semibold text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
              Safety
            </Link>

            <Link to="/contact" className="text-[15px] font-semibold text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
              Contact
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 bg-accent-500 text-white text-[14px] font-semibold rounded-full hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer shadow-sm shadow-accent-500/20"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer relative z-[110]"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            <i className={`text-2xl text-foreground-950 transition-transform duration-200 ${mobileOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
          </button>
        </nav>
      </header>

      {mobileOpen && (
        <div
          ref={mobileMenuRef}
          className="md:hidden fixed inset-0 z-[100]"
        >
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={closeMobileMenu}
          />
          <div className="absolute top-0 right-0 bottom-0 w-full max-w-[320px] bg-white shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between px-4 h-[72px] border-b border-background-200/70">
              <span className="font-heading text-base font-extrabold text-foreground-950">Menu</span>
              <button
                onClick={closeMobileMenu}
                className="w-10 h-10 flex items-center justify-center cursor-pointer rounded-lg hover:bg-background-100 transition-colors"
              >
                <i className="ri-close-line text-xl text-foreground-700"></i>
              </button>
            </div>

            <div className="px-4 py-4 flex flex-col gap-1">
              <Link
                to="/"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-4 py-3.5 bg-primary-50 text-primary-700 rounded-xl font-semibold text-[15px] cursor-pointer"
              >
                <span className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-lg">
                  <i className="ri-home-4-line text-primary-600 text-sm"></i>
                </span>
                Home
              </Link>

              <div className="mt-2">
                <button
                  onClick={() => setCategoryOpen(!categoryOpen)}
                  className="flex items-center justify-between w-full px-4 py-3.5 text-foreground-700 hover:bg-background-100 rounded-xl font-semibold text-[15px] cursor-pointer"
                >
                  <span className="flex items-center gap-3">
                    <span className="w-8 h-8 flex items-center justify-center bg-secondary-50 rounded-lg">
                      <i className="ri-grid-line text-secondary-600 text-sm"></i>
                    </span>
                    Categories
                  </span>
                  <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${categoryOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {categoryOpen && (
                  <div className="ml-4 pl-4 border-l-2 border-primary-200 mt-1 flex flex-col gap-0.5">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={closeMobileMenu}
                        className="flex items-center gap-2.5 px-3 py-2.5 text-[14px] text-foreground-600 hover:bg-primary-50 hover:text-primary-700 rounded-lg cursor-pointer"
                      >
                        <i className={`${cat.icon} text-primary-500 text-xs`}></i>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-3 border-t border-background-100 pt-4">
                <p className="px-4 text-[11px] text-foreground-400 uppercase tracking-widest font-semibold mb-3">Shopping Guide</p>
                <div className="flex flex-col gap-0.5">
                  {blogLinks.map((blog) => (
                    <Link
                      key={blog.slug}
                      to={blog.slug}
                      onClick={closeMobileMenu}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-[14px] text-foreground-600 hover:bg-primary-50 hover:text-primary-700 rounded-lg cursor-pointer"
                    >
                      <i className="ri-article-line text-primary-500 text-xs"></i>
                      {blog.name}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-1 border-t border-background-100 pt-3">
                <Link
                  to="/safety-guide"
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3 px-4 py-3.5 text-foreground-700 hover:bg-background-100 rounded-xl font-semibold text-[15px] cursor-pointer"
                >
                  <span className="w-8 h-8 flex items-center justify-center bg-accent-50 rounded-lg">
                    <i className="ri-shield-check-line text-accent-600 text-sm"></i>
                  </span>
                  Safety Guide
                </Link>

                <Link
                  to="/contact"
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3 px-4 py-3.5 text-foreground-700 hover:bg-background-100 rounded-xl font-semibold text-[15px] cursor-pointer"
                >
                  <span className="w-8 h-8 flex items-center justify-center bg-secondary-50 rounded-lg">
                    <i className="ri-mail-line text-secondary-600 text-sm"></i>
                  </span>
                  Contact
                </Link>
              </div>

              <div className="mt-4 px-4">
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobileMenu}
                  className="flex items-center justify-center gap-2 w-full px-5 py-3.5 bg-accent-500 text-white text-[14px] font-semibold rounded-full hover:bg-accent-600 transition-all cursor-pointer"
                >
                  <i className="ri-shopping-bag-line"></i> Buy It Now
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}